import apiClient from './api';

export interface MotifRefoulement {
  id: string;
  libelle: string;
  description: string;
  categorie: string;
  criticite: 'Critique' | 'Haute' | 'Moyenne' | 'Faible';
  code: string;
}

export interface SettingsResponse {
  success: boolean;
  message: string;
  data: any;
}

class SettingsService {
  /**
   * Récupérer les motifs de refoulement
   */
  async getMotifsRefoulement(): Promise<MotifRefoulement[]> {
    try {
      // Utiliser l'action personnalisée du ViewSet
      const response = await apiClient.get('/settings/motif_refoulee/');
      
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        return response.data;
      } else {
        console.error('Structure de réponse inattendue pour les motifs de refoulement:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des motifs de refoulement:', error);
      return [];
    }
  }

  /**
   * Récupérer un paramètre par son nom
   */
  async getSetting(settingName: string): Promise<any> {
    try {
      const response = await apiClient.get(`/settings/${settingName}/`);
      
      if (response.data) {
        return response.data;
      } else {
        console.error('Paramètre non trouvé:', settingName);
        return null;
      }
    } catch (error) {
      console.error(`Erreur lors de la récupération du paramètre ${settingName}:`, error);
      return null;
    }
  }

  /**
   * Récupérer les motifs de refoulement par catégorie
   */
  async getMotifsRefoulementByCategorie(): Promise<Record<string, MotifRefoulement[]>> {
    try {
      const motifs = await this.getMotifsRefoulement();
      const motifsByCategorie: Record<string, MotifRefoulement[]> = {};
      
      motifs.forEach(motif => {
        if (!motifsByCategorie[motif.categorie]) {
          motifsByCategorie[motif.categorie] = [];
        }
        motifsByCategorie[motif.categorie].push(motif);
      });
      
      return motifsByCategorie;
    } catch (error) {
      console.error('Erreur lors du groupement des motifs par catégorie:', error);
      return {};
    }
  }

  /**
   * Récupérer les motifs de refoulement par criticité
   */
  async getMotifsRefoulementByCriticite(): Promise<Record<string, MotifRefoulement[]>> {
    try {
      const motifs = await this.getMotifsRefoulement();
      const motifsByCriticite: Record<string, MotifRefoulement[]> = {};
      
      motifs.forEach(motif => {
        if (!motifsByCriticite[motif.criticite]) {
          motifsByCriticite[motif.criticite] = [];
        }
        motifsByCriticite[motif.criticite].push(motif);
      });
      
      return motifsByCriticite;
    } catch (error) {
      console.error('Erreur lors du groupement des motifs par criticité:', error);
      return {};
    }
  }

  /**
   * Rechercher des motifs de refoulement
   */
  async searchMotifsRefoulement(query: string): Promise<MotifRefoulement[]> {
    try {
      const motifs = await this.getMotifsRefoulement();
      const searchQuery = query.toLowerCase();
      
      return motifs.filter(motif => 
        motif.libelle.toLowerCase().includes(searchQuery) ||
        motif.description.toLowerCase().includes(searchQuery) ||
        motif.categorie.toLowerCase().includes(searchQuery) ||
        motif.code.toLowerCase().includes(searchQuery)
      );
    } catch (error) {
      console.error('Erreur lors de la recherche de motifs:', error);
      return [];
    }
  }

  /**
   * Sauvegarder plusieurs paramètres en une seule requête
   */
  async bulkSaveSettings(settings: Record<string, any>): Promise<{success: boolean, message: string}> {
    try {
      const response = await apiClient.post('/settings/bulk_save/', {
        settings: settings
      });
      
      if (response.data && response.data.success) {
        return {
          success: true,
          message: response.data.message || 'Paramètres sauvegardés avec succès'
        };
      } else {
        return {
          success: false,
          message: response.data?.message || 'Erreur lors de la sauvegarde'
        };
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde en lot:', error);
      return {
        success: false,
        message: 'Erreur lors de la sauvegarde des paramètres'
      };
    }
  }
}

export default new SettingsService();
